export { preparePriceAdjustments, priceAdjustmentTotal } from './salesPriceAdjustments.mjs';
import { pricedAmount, validateLineDiscount, discountRemark, applyDiscountReturn } from './salesRowPricing.mjs';
export { applyDiscountReturn } from './salesRowPricing.mjs';
export function prepareRowPricing(order, orders) {
  for (const item of order.items) {
    validateLineDiscount(item);
    if (item.lineDiscount) item.amount = pricedAmount(item);
  }
  // Process new returns in row order without counting their not-yet-priced siblings.
  const priced = [];
  for (const item of order.items) {
    priced.push(applyDiscountReturn(item, order.id, priced, orders));
  }
  order.items = priced;
}
const zero = () => ({ amount: 0, cost: 0, profit: 0 });
export const roundMoney = value => Math.round((value + Number.EPSILON) * 100) / 100;
export const lineLabels = { normal: '正常', return: '退货', exchange: '换货', outOfStock: '缺货', missed: '漏上', resend: '补发', cancelled: '客户不要' };
export const isDeferredMissed = item => item.lineType === 'missed' && item.aftercare?.chargeMode === 'deferred';
export const isCurrentResend = item => item.lineType === 'resend' && item.aftercare?.chargeMode === 'current';
export const isUnchargedResend = item => item.lineType === 'resend' && !isCurrentResend(item);
export const resendChargeMode = original => isDeferredMissed(original) ? 'current' : 'original';
export function matchesSourceItem(item, original) {
  if (!original || original.sku !== item.sku || original.subUnit !== item.subUnit) return false;
  if (original.specName === item.specName) return true;
  const packSize = spec => {
    const text = String(spec || '').replace(/\s+/g, '').replaceAll('＝', '=');
    if (/^\d+(?:\.\d+)?$/.test(text)) return Number(text);
    const match = /^1件=(\d+(?:\.\d+)?)(.+)$/.exec(text);
    return match && match[2] === item.subUnit ? Number(match[1]) : NaN;
  };
  const size = packSize(item.specName);
  return size > 0 && size === packSize(original.specName);
}
export function sourceFingerprint(item) {
  const parts = [item.sku, item.specName, item.subUnit, item.subUnitQty, item.amount, item.costPrice, item.historicalImport || null];
  if (item.aftercare?.chargeMode) parts.push(item.aftercare.chargeMode);
  if (item.lineDiscount) parts.push(item.lineDiscount);
  return JSON.stringify(parts);
}
export function lineEffect(item) {
  if (item.historicalImport?.version === 1) {
    const h = item.historicalImport;
    return { amount: h.amount ?? 0, cost: h.cost ?? 0, profit: h.profit ?? 0 };
  }
  const a = item.aftercare;
  if (item.lineType === 'return' && a) {
    const amount = a.deduction === 'current' ? -Math.abs(item.amount) : 0;
    const profit = a.profitTreatment === 'current' && Number.isFinite(a.sourceCost)
      ? roundMoney(-Math.abs(item.amount) + a.sourceCost * Math.abs(item.subUnitQty)) : 0;
    return { amount: roundMoney(amount), cost: roundMoney(amount - profit), profit };
  }
  if (isDeferredMissed(item) || (item.lineType === 'resend' && !isCurrentResend(item))) return zero();
  if (['return', 'exchange', 'outOfStock', 'cancelled'].includes(item.lineType)) return zero();
  const amount = item.paid || item.printPrice === false ? 0 : pricedAmount(item);
  const cost = (item.costPrice || 0) * item.subUnitQty;
  const profit = pricedAmount(item) - cost;
  return { amount: roundMoney(amount), cost: roundMoney(cost), profit: roundMoney(profit) };
}
export function validateRelations(order, orders) {
  const seen = new Set();
  const others = orders.filter(o => o.id !== order.id);
  const allItems = [...others.flatMap(o => o.items), ...order.items];
  for (const item of order.items) {
    validateLineDiscount(item);
    const a = item.aftercare;
    if (a && item.historicalImport) throw Error('历史核算行不能直接改为售后，请新增独立退货或补发行并关联原单');
    if (!a) {
      if (['missed', 'resend', 'cancelled'].includes(item.lineType)) throw Error('请填写处理记录');
      continue;
    }
    if (!['return', 'missed', 'resend', 'cancelled'].includes(item.lineType)) throw Error('处理记录与行类型不一致');
    if (!a.id || seen.has(a.id) || others.some(o => o.items.some(i => i.aftercare?.id === a.id))) throw Error('处理记录重复，请关联原记录，不要重复新增');
    seen.add(a.id);
    if (a.chargeMode != null && !(item.lineType === 'missed' ? ['deferred', 'original'] : item.lineType === 'resend' ? ['current', 'original'] : []).includes(a.chargeMode)) throw Error('计费方式与行类型不一致');
    if (!Number.isFinite(item.subUnitQty) || item.subUnitQty <= 0 || !Number.isFinite(item.amount) || item.amount < 0) throw Error('处理数量必须大于零，金额不能为负数');
    if (item.lineType === 'return') {
      if (!['previous', 'current', 'pending'].includes(a.deduction) || !['previous', 'current', 'pending'].includes(a.profitTreatment)) throw Error('请选择退货扣款及利润处理方式');
      if (a.profitTreatment === 'current' && !Number.isFinite(a.sourceCost)) throw Error('缺少原成本，请先关联原单或将利润标为待核实');
    }
    if (!a.sourceOrderId) {
      if (item.lineType === 'resend') throw Error('补发必须关联原单');
      continue;
    }
    const source = others.find(o => o.id === a.sourceOrderId);
    if (!source || source.customerId !== order.customerId) throw Error('原单不存在或客户不一致');
    if (!Number.isInteger(a.sourceLineIndex)) throw Error('请选择原单商品');
    const original = source.items[a.sourceLineIndex];
    if (!matchesSourceItem(item, original)) throw Error('原单商品、规格或单位不一致，请重新选择');
    if (a.sourceFingerprint && a.sourceFingerprint !== sourceFingerprint(original)) throw Error('原单商品数量或成本已变化，请重新核对原单关联');
    if (!['normal', 'missed', undefined].includes(original.lineType)) throw Error('原单商品不是可退货或补发的销售行');
    if (!['return', 'resend'].includes(item.lineType)) throw Error('此行类型不需要关联其他订单');
    if (item.lineType === 'resend' && (a.chargeMode || 'original') !== resendChargeMode(original)) throw Error('补发计费方式与原单不一致，请重新关联');
    if (item.lineType === 'return' && isDeferredMissed(original)) throw Error('漏上商品尚未计费，请直接取消漏上记录，无需退货扣款');
    const used = allItems.filter(i => i.lineType === item.lineType && i.aftercare?.sourceOrderId === a.sourceOrderId && i.aftercare?.sourceLineIndex === a.sourceLineIndex).reduce((s, i) => s + Math.abs(i.subUnitQty), 0);
    if (used > Math.abs(original.subUnitQty) + 0.000001) throw Error('累计处理数量超过原单数量，可能重复退货或补发');
    if (item.lineType === 'return' && (original.lineDiscount || orders.some(o => o.priceAdjustments?.some(p => p.sourceOrderId === a.sourceOrderId && p.sourceLineIndex === a.sourceLineIndex)))) {
      const expected = applyDiscountReturn(item, order.id, order.items, orders).amount;
      if (Math.abs(item.amount - expected) > 0.001) throw Error('退货金额需按优惠成交金额分摊，请重新核对');
    }
  }
}
export function aftercareRemark(item) {
  const a = item.aftercare;
  if (!a) return '';
  const origin = a.sourceSalesId ? `原单 ${a.sourceSalesId}；` : '';
  if (item.lineType === 'return') {
    const deduction = { previous: '原单已扣，本单不再扣', current: '本单抵扣', pending: '待抵扣' }[a.deduction];
    const profit = { previous: '利润原单已处理', current: '利润本次冲减', pending: '利润待核实' }[a.profitTreatment];
    return `【退货】${origin}¥${Math.abs(item.amount).toFixed(2)}；${deduction}；${profit}`;
  }
  if (item.lineType === 'resend') return !a.sourceOrderId ? '【补发】待关联原单，待确认计费' : `【补发】${origin}${isCurrentResend(item) ? '本单计费' : `${a.sourcePayment || '付款情况以原单为准'}；本单不重复收费`}`;
  if (item.lineType === 'missed') return '漏上';
  if (item.lineType === 'cancelled') return '【客户不要】本行取消，不计入本单金额';
  return '';
}
export function displayRemark(item) {
  const reason = item.lineType === 'return' && typeof item.aftercare?.reason === 'string' ? item.aftercare.reason.trim() : '';
  return [aftercareRemark(item), discountRemark(item), reason ? `退货原因：${reason}` : '', item.remark].filter(Boolean).join('；');
}
export async function saveAndReset(save, reset) { await save(); reset(); }
